import React, { useState, MouseEvent } from 'react'
import {
  NewGridTable,
  IGridTableColumn,
  Skeleton,
  IconButton,
  Button,
  Details,
  Toaster,
  Confirmation,
  Dialog,
} from '@ui'
import { FormGroup, InputGroup, Classes, Intent, Tag, MenuItem } from '@blueprintjs/core'
import { Select2 } from '@blueprintjs/select'
import { NavigateFn } from '@reach/router'
import { UseSpaceTypesHook } from './hooks/useSpaceTypes'
import { ISpaceTypeWithCompany, graphQLError } from '@api'
import api from '@api'
import { useReload } from '@hooks/reloadContext'
import track from '@track'
import Event from '@track/Event'
import css from './tableView.module.scss'

enum InteractivityType {
  SEATABLE = 'seatable',
  ALLOCATABLE = 'allocatable',
}

export function handleErrors(errors: { graphQLErrors: graphQLError[] }, returnDivs: boolean) {
  const errorMessage: string[] = []
  if (errors && errors.graphQLErrors?.length) {
    errors.graphQLErrors.forEach((gqlErr) => {
      if (gqlErr.unknownErrors && Object.keys(gqlErr.unknownErrors).length) {
        gqlErr.unknownErrors.forEach((ue) => errorMessage.push(ue.message))
      }
      if (gqlErr.invalidArguments && Object.keys(gqlErr.invalidArguments).length) {
        Object.values(gqlErr.invalidArguments).forEach((ia) => {
          errorMessage.push(ia)
        })
      }
    })
    if (!errorMessage.length)
      for (const { message } of errors.graphQLErrors) {
        errorMessage.push(message)
      }
  }

  if (returnDivs) return errorMessage.map((e, i) => <div key={i}>{e}</div>)
  return errorMessage
}

export default function TableView({
  spaceTypes,
  navigate,
}: {
  spaceTypes: UseSpaceTypesHook
  navigate?: NavigateFn
}) {
  const [interactivityType, setInteractivityType] = useState<InteractivityType>(
    InteractivityType.ALLOCATABLE
  )
  const [showNew, setShowNew] = useState(false)
  const [name, setName] = useState('')
  const [errors, setErrors] = useState<React.ReactNode[]>([])
  const [loading, setLoading] = useState(false)
  const reload = useReload()

  const onRemove = (id: string) =>
    api.space.spaceType
      .remove(id)
      .then(() => {
        reload.spaceTypes()
      })
      .catch((error: { graphQLErrors: graphQLError[] }) => {
        const stringErrors = handleErrors(error, false) as string[]
        stringErrors.forEach((e: string) => Toaster.fail({ message: e }))
      })

  const columns: IGridTableColumn<ISpaceTypeWithCompany>[] = [
    {
      label: 'Name',
      sort: 'displayName',
      Cell: ({ cell, skeleton }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <div className={`flex justify-start items-center ${css.tableCell}`}>
            <div
              className="table__link"
              onClick={() => {
                navigate?.(`?spaceTypeId=${cell?.data.id}`)
                track.spaceTypes({ eventName: Event.ROW_CLICK })
              }}
            >
              {cell?.data.displayName}
            </div>
            {cell?.data.hidden && (
              <Tag minimal className="ml-1">
                Hidden
              </Tag>
            )}
          </div>
        ),
    },
    {
      label: 'Type',
      Cell: ({ cell, skeleton }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <div className={`flex justify-start items-center ${css.tableCell}`}>
            {cell?.data?.company ? (
              <Tag minimal round intent="success" className="ml-1">
                Custom
              </Tag>
            ) : (
              <Tag minimal round intent="primary" className="ml-1">
                Default
              </Tag>
            )}
          </div>
        ),
    },
    {
      label: 'Seatable?',
      Cell: ({ cell, skeleton }) =>
        skeleton ? (
          <Skeleton />
        ) : (
          <div className={`flex justify-start items-center ${css.tableCell}`}>
            {cell?.data?.interactivityType === 2 && (
              <Tag minimal round intent="primary" className="ml-1">
                Seatable
              </Tag>
            )}
          </div>
        ),
    },
    {
      label: '',
      Cell: ({ cell }) => {
        if (!cell || !cell.data.company) return null
        return (
          <div className="flex justify-end" style={{ width: '100%' }}>
            <Confirmation
              position="top-right"
              onConfirm={() => onRemove(cell.data.id)}
              cancelable
              text={<div className={css.deleteSpace}>Delete this Space Type?</div>}
            >
              <IconButton iconName="times" tooltip="Delete" minimal />
            </Confirmation>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className={`flex items-center justify-end my-2 ${css.spaceTypeToolbar}`}>
        <Button
          className="ml2"
          text="New Space Type"
          data-button="new_space_type"
          iconName="plus"
          onClick={() => {
            setShowNew(true)
          }}
        />
      </div>
      <NewGridTable<ISpaceTypeWithCompany>
        status={spaceTypes.status}
        columns={columns}
        data={spaceTypes.items}
        page={{
          hasNextPage: spaceTypes.pageInfo.hasNextPage,
          endCursor: spaceTypes.pageInfo.endCursor,
          onNextPage: spaceTypes.nextPage,
        }}
        sort={{
          column: spaceTypes.sort.column,
          order: spaceTypes.sort.order,
          onSort: (column: any) => {
            spaceTypes.sortBy(column)
            track.spaceTypes({
              eventName: Event.SORT,
              props: { columnName: column },
            })
          },
        }}
        headerTop={52}
      />
      <Dialog
        isOpen={showNew}
        title="New Space Type"
        usePortal
        isCloseButtonShown={false}
        toolbar={
          <div className="dialog-buttons">
            <IconButton
              minimal
              tooltip="Close"
              iconName="times"
              onClick={() => setShowNew(false)}
            />
          </div>
        }
        body={
          <Details
            onSubmit={(e) => {
              e.preventDefault()
              setLoading(true)
              api.space.spaceType
                .create(name.trim(), interactivityType)
                .then(() => {
                  setName('')
                  setLoading(false)
                  setErrors([])
                  reload.spaceTypes()
                  Toaster.success({ message: 'Space Type Created' })
                  setShowNew(false)
                })
                .catch((error: { graphQLErrors: graphQLError[] }) => {
                  setLoading(false)
                  setErrors(handleErrors(error, true))
                })
            }}
          >
            <div className={Classes.DIALOG_BODY}>
              <Details.Row>
                <Details.Label label="Name" required />
                <Details.Column>
                  <FormGroup helperText={errors} intent={errors.length ? Intent.DANGER : undefined}>
                    <InputGroup
                      autoFocus
                      value={name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setName(event.target.value)
                      }
                    />
                  </FormGroup>
                </Details.Column>
              </Details.Row>
              <Details.Row>
                <Details.Label label="Seatable?" required />
                <Details.Column>
                  <SeatableSelect
                    onItemSelect={(it) => setInteractivityType(it)}
                    value={interactivityType}
                  />
                </Details.Column>
              </Details.Row>
            </div>
            <div className={Classes.DIALOG_FOOTER}>
              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                <Button text="Create" intent="primary" type="submit" loading={loading} />
              </div>
            </div>
          </Details>
        }
      />
    </>
  )
}

const SeatableSelector = Select2<{
  value: InteractivityType
  display: string
}>

function SeatableSelect({
  onItemSelect,
  value,
}: {
  onItemSelect: (i: InteractivityType) => void
  value: InteractivityType
}) {
  const options = [
    { value: InteractivityType.SEATABLE, display: 'Yes' },
    { value: InteractivityType.ALLOCATABLE, display: 'No' },
  ]

  return (
    <SeatableSelector
      filterable={false}
      popoverProps={{ minimal: true }}
      items={options}
      itemRenderer={(item, { handleClick, modifiers }) => (
        <MenuItem
          key={`item-${item.value}`}
          active={modifiers.active}
          onClick={(e: MouseEvent<HTMLElement>) => {
            handleClick(e)
          }}
          text={item.display}
        />
      )}
      onItemSelect={({ value }) => onItemSelect(value)}
    >
      <Button text={options.find((o) => o.value === value)?.display} />
    </SeatableSelector>
  )
}
